import { IconDefinition } from "@fortawesome/fontawesome-svg-core";

import { UserDto } from "../../DTOs/User/UserDto";
import { RoleConfig } from "../../DTOs/Settings/RoleConfigDto";
import { OFFLINE_CAPABLE_PATHS } from "../../offline/offlinePaths";
import { AppRouteConfig, Editing, pageEditing } from "../../utilities/appRoutes";
import { satisfiesRouteRequirements } from "../../utilities/helpers/routeAccess";
import { customSectionName, routeLabel, sectionName, sectionOverrides, viewName } from "../../i18n/pageNames";
import { getRouteIcon } from "../../utilities/routeIcons";
import type { ModalContextType } from "../Contexts/GlobalModalContext";
import { menuItemKey } from "./navActive";

export interface MenuItem {
  key: string;
  label: string;
  to?: string;
  onClick?: () => void; // instead of linking
  icon?: IconDefinition;
  subItems?: MenuItem[];
  editing: Editing;
  worksOffline: boolean;
}

export interface RowMarks {
  viewOnly: boolean;
  needsConnection: boolean;
}

const routeName = (route: AppRouteConfig, roleConfig: RoleConfig | null): string =>
  (route.section && customSectionName(sectionOverrides(roleConfig), route.section))
  || (route.view && viewName(route.view))
  || (route.section && sectionName(route.section))
  || (route.label && routeLabel(route.label))
  || route.path;

const isRouteAllowed = (route: AppRouteConfig, user: UserDto | null) => {
  if (user?.isSuperAdmin) return true;
  if (!route.protected) return true;
  return satisfiesRouteRequirements(route, user);
};

const combinedEditing = (editing: Editing[]): Editing =>
  editing.includes("canEdit") ? "canEdit" : editing.includes("viewOnly") ? "viewOnly" : "nothingToEdit";

export const buildMenu = (
  routes: AppRouteConfig[],
  user: UserDto | null,
  roleConfig: RoleConfig | null,
  showModal: ModalContextType["showModal"],
  parentPath = '',
  parentKey = ''
): MenuItem[] => {
  const menu: MenuItem[] = [];

  for (const route of routes) {
    if (!route.path || route.path === '*' || route.ignore) continue;
    if (!isRouteAllowed(route, user)) continue;

    const label = routeName(route, roleConfig);
    const key = menuItemKey(parentKey, label);
    const fullPath = `${parentPath}/${route.path}`.replace(/\/+/g, '/'); // Normalize slashes
    const hasChildren = route.children && route.children.length > 0;
    const subItems = hasChildren ? buildMenu(route.children!, user, roleConfig, showModal, fullPath, key) : [];

    const { action } = route;
    const isGroupOnly = !route.element && subItems.length > 0;

    // Note: a pure grouping route (no element of its own, e.g. "Geology" or "Admin") derives its
    // visibility entirely from whether any child survived the access check above, so it is skipped when
    // none did - not merely when it has no children declared.
    if (!route.element && !action && subItems.length === 0) continue;

    const to = action || isGroupOnly ? undefined : fullPath;

    const menuItem: MenuItem = {
      key,
      label,
      icon: route.icon ?? getRouteIcon(fullPath),
      ...(to ? { to } : {}),
      ...(action ? { onClick: () => action(showModal) } : {}),
      ...(subItems.length > 0 ? { subItems } : {}),
      editing: combinedEditing([pageEditing(route, user), ...subItems.map(item => item.editing)]),
      worksOffline: (!!to && OFFLINE_CAPABLE_PATHS.has(to)) || subItems.some(item => item.worksOffline),
    };

    menu.push(menuItem);
  }

  return menu;
};

export const rowMarks = (item: MenuItem, isExpanded: (key: string) => boolean, isOnline: boolean): RowMarks => ({
  viewOnly: item.editing === "viewOnly" && !(item.subItems && isExpanded(item.key)),
  needsConnection: !isOnline && !item.worksOffline,
});

export const shownRows = (items: MenuItem[], isExpanded: (key: string) => boolean): MenuItem[] =>
  items.flatMap(item => [item, ...(item.subItems && isExpanded(item.key) ? shownRows(item.subItems, isExpanded) : [])]);

export const shownMarks = (items: MenuItem[], isExpanded: (key: string) => boolean, isOnline: boolean): RowMarks => {
  const marks = shownRows(items, isExpanded).map(item => rowMarks(item, isExpanded, isOnline));

  return {
    viewOnly: marks.some(mark => mark.viewOnly),
    needsConnection: marks.some(mark => mark.needsConnection),
  };
};
